import { useState, useRef, useEffect, useCallback } from 'react';
import { Plus, X, Save, ChevronDown, Bookmark } from 'lucide-react';
import { FilterChip } from './FilterChip';
import type { FilterField, ActiveFilter } from './FilterChip';

export type { FilterField, ActiveFilter };

interface SavedPreset {
  name: string;
  filters: ActiveFilter[];
}

export interface FilterBarProps {
  fields: FilterField[];
  filters: ActiveFilter[];
  onChange: (filters: ActiveFilter[]) => void;
  storageKey?: string;
}

function defaultOperator(type: FilterField['type']): ActiveFilter['operator'] {
  switch (type) {
    case 'select':
      return 'eq';
    case 'text':
      return 'contains';
    case 'number_range':
      return 'gt';
  }
}

function loadPresets(storageKey: string): SavedPreset[] {
  try {
    const raw = localStorage.getItem(storageKey);
    if (raw) return JSON.parse(raw) as SavedPreset[];
  } catch { /* ignore */ }
  return [];
}

function savePresets(storageKey: string, presets: SavedPreset[]) {
  try {
    localStorage.setItem(storageKey, JSON.stringify(presets));
  } catch { /* ignore */ }
}

export function FilterBar({ fields, filters, onChange, storageKey }: FilterBarProps) {
  const [fieldMenuOpen, setFieldMenuOpen] = useState(false);
  const [savedMenuOpen, setSavedMenuOpen] = useState(false);
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [presetName, setPresetName] = useState('');
  const [presets, setPresets] = useState<SavedPreset[]>(() =>
    storageKey ? loadPresets(storageKey) : [],
  );

  const fieldMenuRef = useRef<HTMLDivElement>(null);
  const savedMenuRef = useRef<HTMLDivElement>(null);
  const saveDialogRef = useRef<HTMLDivElement>(null);

  // Close dropdowns on outside click
  useEffect(() => {
    const handler = (e: MouseEvent) => {
      if (fieldMenuOpen && fieldMenuRef.current && !fieldMenuRef.current.contains(e.target as Node)) {
        setFieldMenuOpen(false);
      }
      if (savedMenuOpen && savedMenuRef.current && !savedMenuRef.current.contains(e.target as Node)) {
        setSavedMenuOpen(false);
      }
      if (saveDialogOpen && saveDialogRef.current && !saveDialogRef.current.contains(e.target as Node)) {
        setSaveDialogOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [fieldMenuOpen, savedMenuOpen, saveDialogOpen]);

  const addFilter = useCallback(
    (field: FilterField) => {
      const newFilter: ActiveFilter = {
        key: field.key,
        operator: defaultOperator(field.type),
        value: '',
      };
      onChange([...filters, newFilter]);
      setFieldMenuOpen(false);
    },
    [filters, onChange],
  );

  const updateFilter = useCallback(
    (index: number, updated: ActiveFilter) => {
      const next = [...filters];
      next[index] = updated;
      onChange(next);
    },
    [filters, onChange],
  );

  const removeFilter = useCallback(
    (index: number) => {
      onChange(filters.filter((_, i) => i !== index));
    },
    [filters, onChange],
  );

  const clearAll = useCallback(() => {
    onChange([]);
  }, [onChange]);

  const handleSavePreset = useCallback(() => {
    if (!storageKey || !presetName.trim() || filters.length === 0) return;
    const newPreset: SavedPreset = { name: presetName.trim(), filters: [...filters] };
    const updated = [...presets.filter((p) => p.name !== newPreset.name), newPreset];
    setPresets(updated);
    savePresets(storageKey, updated);
    setPresetName('');
    setSaveDialogOpen(false);
  }, [storageKey, presetName, filters, presets]);

  const applyPreset = useCallback(
    (preset: SavedPreset) => {
      onChange([...preset.filters]);
      setSavedMenuOpen(false);
    },
    [onChange],
  );

  const deletePreset = useCallback(
    (name: string, e: React.MouseEvent) => {
      e.stopPropagation();
      if (!storageKey) return;
      const updated = presets.filter((p) => p.name !== name);
      setPresets(updated);
      savePresets(storageKey, updated);
    },
    [storageKey, presets],
  );

  return (
    <div className="flex items-center gap-2 flex-wrap">
      {/* Active filter chips */}
      {filters.map((filter, index) => {
        const field = fields.find((f) => f.key === filter.key);
        if (!field) return null;
        return (
          <FilterChip
            key={`${filter.key}-${index}`}
            field={field}
            filter={filter}
            onChange={(updated) => updateFilter(index, updated)}
            onRemove={() => removeFilter(index)}
          />
        );
      })}

      {/* Add filter button */}
      <div className="relative" ref={fieldMenuRef}>
        <button
          type="button"
          onClick={() => setFieldMenuOpen((v) => !v)}
          className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-surface-400 hover:text-surface-200 bg-surface-800 border border-surface-700 border-dashed rounded-lg hover:border-surface-600 transition-colors cursor-pointer"
        >
          <Plus className="h-3 w-3" />
          Add filter
        </button>
        {fieldMenuOpen && (
          <div className="absolute top-full left-0 mt-1 bg-surface-800 border border-surface-600 rounded-lg shadow-xl z-50 min-w-[160px] py-1">
            {fields.map((field) => (
              <button
                key={field.key}
                type="button"
                onClick={() => addFilter(field)}
                className="w-full text-left px-3 py-1.5 text-xs text-surface-300 hover:bg-surface-700 hover:text-surface-100 transition-colors cursor-pointer"
              >
                {field.label}
              </button>
            ))}
          </div>
        )}
      </div>

      {/* Clear all */}
      {filters.length > 0 && (
        <button
          type="button"
          onClick={clearAll}
          className="inline-flex items-center gap-1 px-2 py-1.5 text-xs text-surface-500 hover:text-red-400 transition-colors cursor-pointer"
        >
          <X className="h-3 w-3" />
          Clear all
        </button>
      )}

      {/* Spacer */}
      {storageKey && <div className="flex-1" />}

      {/* Save filter preset */}
      {storageKey && filters.length > 0 && (
        <div className="relative" ref={saveDialogRef}>
          <button
            type="button"
            onClick={() => setSaveDialogOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-surface-400 hover:text-surface-200 transition-colors cursor-pointer"
          >
            <Save className="h-3 w-3" />
            Save filter
          </button>
          {saveDialogOpen && (
            <div className="absolute top-full right-0 mt-1 bg-surface-800 border border-surface-600 rounded-lg shadow-xl z-50 p-3 min-w-[200px]">
              <label className="block text-xs text-surface-400 mb-1.5">Preset name</label>
              <input
                type="text"
                value={presetName}
                onChange={(e) => setPresetName(e.target.value)}
                onKeyDown={(e) => e.key === 'Enter' && handleSavePreset()}
                placeholder="e.g. Hot leads"
                className="w-full px-2.5 py-1.5 bg-surface-900 border border-surface-700 rounded-md text-xs text-surface-200 placeholder-surface-500 focus:outline-none focus:ring-1 focus:ring-nexus-500 mb-2"
                autoFocus
              />
              <button
                type="button"
                onClick={handleSavePreset}
                disabled={!presetName.trim()}
                className="w-full px-3 py-1.5 text-xs font-medium bg-nexus-600 text-white rounded-md hover:bg-nexus-500 disabled:opacity-50 disabled:pointer-events-none transition-colors cursor-pointer"
              >
                Save
              </button>
            </div>
          )}
        </div>
      )}

      {/* Saved presets dropdown */}
      {storageKey && presets.length > 0 && (
        <div className="relative" ref={savedMenuRef}>
          <button
            type="button"
            onClick={() => setSavedMenuOpen((v) => !v)}
            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 text-xs font-medium text-surface-400 hover:text-surface-200 transition-colors cursor-pointer"
          >
            <Bookmark className="h-3 w-3" />
            Saved
            <ChevronDown className="h-3 w-3" />
          </button>
          {savedMenuOpen && (
            <div className="absolute top-full right-0 mt-1 bg-surface-800 border border-surface-600 rounded-lg shadow-xl z-50 min-w-[180px] py-1">
              {presets.map((preset) => (
                <div
                  key={preset.name}
                  className="flex items-center justify-between group"
                >
                  <button
                    type="button"
                    onClick={() => applyPreset(preset)}
                    className="flex-1 text-left px-3 py-1.5 text-xs text-surface-300 hover:bg-surface-700 hover:text-surface-100 transition-colors cursor-pointer"
                  >
                    {preset.name}
                    <span className="text-surface-500 ml-1">
                      ({preset.filters.length})
                    </span>
                  </button>
                  <button
                    type="button"
                    onClick={(e) => deletePreset(preset.name, e)}
                    className="px-2 py-1.5 text-surface-600 hover:text-red-400 opacity-0 group-hover:opacity-100 transition-all cursor-pointer"
                  >
                    <X className="h-3 w-3" />
                  </button>
                </div>
              ))}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
